/**
 * StoryPreview — the pop-out preview window (spec 009), opened by the story
 * editor's preview pane with ⤴.
 *
 * - `?sceneId=` plays that scene (`SingleScenePreview`); opened on its own it
 *   loads the saved scene and works standalone.
 * - `?storyId=&mode=runner` plays the story runner (`StoryRunnerPreview`),
 *   from `storySceneId` when given (the scene the pane's runner was at).
 * - Neither: "Nothing to preview".
 *
 * BroadcastChannel `sm-story-preview` keeps it in step with the editor:
 * orientation both ways, `scene-update` (the editor's unsaved scene state,
 * applied over the saved one) and `close`; it posts `popout-ready` on load and
 * `popout-closed` when it goes.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { StoryApiResponse, StoryPackageManifest } from '../../../Shared/src/@types';
import type { PlayableSceneInput } from '../../../Shared/src/runtime/playableScene';
import { apiClient } from '../api/client';
import { PREVIEW_CHANNEL, type PreviewMessage } from '../utils/previewSources';
import { buildPreviewManifest } from '../utils/previewRunner';
import { sceneSpriteMediaIds, useMediaSheetStore } from '../stores/mediaSheetStore';
import SingleScenePreview from '../components/preview/SingleScenePreview';
import StoryRunnerPreview from '../components/preview/StoryRunnerPreview';
import styles from './StoryPreview.module.scss';

type OrientationMode = 'desktop' | 'portrait' | 'landscape';

const ORIENTATION_LABELS: Record<OrientationMode, string> = {
  desktop:   '🖥',
  portrait:  '📱',
  landscape: '📱',
};

const toId = (v: string | null) => {
  const n = v ? parseInt(v, 10) : NaN;
  return Number.isFinite(n) && n > 0 ? n : null;
};

const StoryPreview: React.FC = () => {
  const params = useMemo(() => new URLSearchParams(window.location.search), []);
  const sceneId = toId(params.get('sceneId'));
  const storyId = toId(params.get('storyId'));
  const runnerMode = params.get('mode') === 'runner' && !!storyId;
  const startAt = toId(params.get('storySceneId'));
  const sceneName = params.get('sceneName');
  const initial = params.get('orientation');

  const [orientation, setOrientation] = useState<OrientationMode>(
    initial === 'desktop' || initial === 'landscape' ? initial : 'portrait',
  );
  const channelRef = useRef<BroadcastChannel | null>(null);

  /** The editor's unsaved scenes by scene id; they win over the saved ones. */
  const [overrides, setOverrides] = useState<Record<number, PlayableSceneInput>>({});

  // ── Single scene: the saved scene, then the editor's state ────────────────
  const [saved, setSaved] = useState<PlayableSceneInput | null>(null);
  const [loading, setLoading] = useState(!!sceneId && !runnerMode);
  useEffect(() => {
    if (!sceneId || runnerMode) return;
    let cancelled = false;
    void apiClient.getScene(sceneId).then(
      (res) => { if (!cancelled && res.success) setSaved(res.data as unknown as PlayableSceneInput); },
      () => undefined,
    ).finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [sceneId, runnerMode]);
  const scene = sceneId ? overrides[sceneId] ?? saved : null;

  // ── Runner: the story and its scenes ──────────────────────────────────────
  const [story, setStory] = useState<StoryApiResponse | null>(null);
  const [savedScenes, setSavedScenes] = useState<Record<number, PlayableSceneInput> | null>(null);
  useEffect(() => {
    if (!runnerMode || !storyId) return;
    let cancelled = false;
    void (async () => {
      try {
        const res = await apiClient.getStory(storyId);
        if (cancelled || !res.success) return;
        const s = res.data as StoryApiResponse;
        const onGraph = new Set(s.graph.nodes.filter((n) => n.type !== 'end').map((n) => n.storySceneId));
        const rows = s.storyScenes.filter((ss) => onGraph.has(ss.id));
        const scenes = await Promise.all(rows.map((ss) => apiClient.getScene(ss.sceneId).then((r) => (r.success ? r.data : null), () => null)));
        if (cancelled) return;
        const byStoryScene: Record<number, PlayableSceneInput> = {};
        rows.forEach((ss, i) => {
          const sc = scenes[i];
          if (sc) byStoryScene[ss.id] = sc as unknown as PlayableSceneInput;
        });
        setStory(s);
        setSavedScenes(byStoryScene);
      } catch { /* "Nothing to preview" stays */ }
    })();
    return () => { cancelled = true; };
  }, [runnerMode, storyId]);

  // The runner's manifest is built once, with the edits known by then
  const [manifest, setManifest] = useState<StoryPackageManifest | null>(null);
  const overridesRef = useRef(overrides);
  overridesRef.current = overrides;
  useEffect(() => {
    if (!story || !savedScenes || manifest) return;
    // Give the editor a moment to send its unsaved scenes
    let cancelled = false;
    const t = setTimeout(() => {
      const merged: Record<number, PlayableSceneInput> = {};
      for (const ss of story.storyScenes) {
        const sc = overridesRef.current[ss.sceneId] ?? savedScenes[ss.id];
        if (sc) merged[ss.id] = { ...sc, name: sc.name || ss.sceneName || `Scene #${ss.sceneId}` };
      }
      // The spritesheet cuts of the scenes' pictures (spec 033) before the manifest is built.
      const ids = Object.values(merged).flatMap((sc) => sceneSpriteMediaIds(sc as never));
      void useMediaSheetStore.getState().loadSheets(ids).then(() => {
        if (!cancelled) setManifest(buildPreviewManifest(story, merged));
      });
    }, 200);
    return () => { cancelled = true; clearTimeout(t); };
  }, [story, savedScenes, manifest]);

  // ── Channel ───────────────────────────────────────────────────────────────
  useEffect(() => {
    if (typeof BroadcastChannel === 'undefined') return;
    const bc = new BroadcastChannel(PREVIEW_CHANNEL);
    channelRef.current = bc;
    bc.onmessage = (ev: MessageEvent<PreviewMessage>) => {
      const m = ev.data;
      if (m?.type === 'orientation-change' && m.orientation) setOrientation(m.orientation);
      else if (m?.type === 'scene-update' && m.scene) setOverrides((prev) => ({ ...prev, [m.sceneId]: m.scene }));
      else if (m?.type === 'close') window.close();
    };
    try { bc.postMessage({ type: 'popout-ready' } satisfies PreviewMessage); } catch { /* closed */ }
    const handleUnload = () => {
      try { bc.postMessage({ type: 'popout-closed' } satisfies PreviewMessage); } catch { /* closed */ }
    };
    window.addEventListener('beforeunload', handleUnload);
    return () => {
      window.removeEventListener('beforeunload', handleUnload);
      bc.close();
      channelRef.current = null;
    };
  }, []);

  const handleOrientationChange = useCallback((mode: OrientationMode) => {
    setOrientation(mode);
    try { channelRef.current?.postMessage({ type: 'orientation-change', orientation: mode } satisfies PreviewMessage); } catch { /* closed */ }
  }, []);

  const handlePopBack = useCallback(() => window.close(), []);

  if (!sceneId && !storyId) {
    return <div className={styles.error} data-testid="preview-nothing">Nothing to preview</div>;
  }

  const title = runnerMode
    ? `Story: ${story?.name ?? ''}`
    : `Preview: ${sceneName || (scene?.name as string | undefined) || 'Scene'}`;

  return (
    <div className={styles.container} data-testid="story-preview-window">
      <div className={styles.header}>
        <span className={styles.headerTitle}>{title}</span>
        <div className={styles.headerActions}>
          <div className={styles.orientationGroup}>
            {(['desktop', 'portrait', 'landscape'] as OrientationMode[]).map((mode) => (
              <button
                key={mode}
                className={`${styles.orientationBtn} ${orientation === mode ? styles.orientationActive : ''}`}
                onClick={() => handleOrientationChange(mode)}
                title={mode.charAt(0).toUpperCase() + mode.slice(1)}
                aria-pressed={orientation === mode}
                data-testid={`preview-orientation-${mode}`}
              >
                <span className={mode === 'landscape' ? styles.rotatedIcon : undefined}>
                  {ORIENTATION_LABELS[mode]}
                </span>
              </button>
            ))}
          </div>
          <button className={styles.headerBtn} onClick={handlePopBack} title="Pop back">⤶</button>
        </div>
      </div>

      {runnerMode ? (
        story && manifest ? (
          <StoryRunnerPreview
            story={story}
            manifest={manifest}
            orientation={orientation}
            startAtStorySceneId={startAt}
            onStop={handlePopBack}
            wrapClassName={styles.canvasWrap}
          />
        ) : (
          <div className={styles.canvasWrap}><div className={styles.loading}><div className={styles.spinner} /></div></div>
        )
      ) : sceneId && !loading && !scene ? (
        <div className={styles.error}>This scene could not be loaded</div>
      ) : (
        <SingleScenePreview
          scene={scene}
          loading={loading}
          orientation={orientation}
          wrapClassName={styles.canvasWrap}
        />
      )}
    </div>
  );
};

export default StoryPreview;
